import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { reportsAPI, modalitiesAPI, bodyPartsAPI } from '../../services/api';
import FilterDropdown from '../common/FilterDropdown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import './ReportsList.css';

const PAGE_SIZE = 10;

const fmtShort = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : null;

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'non_hospital', label: 'Non Hospital' },
];

const ReportsList = () => {
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({});
  const [firstLoad, setFirstLoad] = useState(true);  
  const [refreshing, setRefreshing] = useState(false); 
  const [page, setPage] = useState(1);
  const requestId = useRef(0); 

  // Search + filters
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [reportType, setReportType] = useState('');
  const [modalityId, setModalityId] = useState('');
  const [bodyPartId, setBodyPartId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const dateRangeRef = useRef(null);
  const [modalities, setModalities] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);

  useEffect(() => {
    const onClick = (e) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(e.target)) setDateRangeOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => { modalitiesAPI.getAll().then(r => setModalities(r.data || [])).catch(() => { }); }, []);
  useEffect(() => { bodyPartsAPI.getAll().then(r => setBodyParts(r.data || [])).catch(() => { }); }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const activeFilterCount = [status, reportType, modalityId, bodyPartId, dateFrom, dateTo].filter(Boolean).length;
  const dateRangeLabel = (dateFrom || dateTo) ? `${fmtShort(dateFrom) || '…'} - ${fmtShort(dateTo) || '…'}` : 'Select';

  const fetchPage = useCallback((p, filters) => {
    const myId = ++requestId.current;
    setRefreshing(true);
    const params = { page: p, limit: PAGE_SIZE };
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.reportType) params.report_type = filters.reportType;
    if (filters.modalityId) params.modality_id = filters.modalityId;
    if (filters.bodyPartId) params.body_part_id = filters.bodyPartId;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;

    reportsAPI.getAll(params)
      .then(r => {
        if (myId !== requestId.current) return; 
        setReports(r.data?.data || []);
        setPagination(r.data?.pagination || {});
        setPage(p);
        setFirstLoad(false);
      })
      .catch(() => { })
      .finally(() => { if (myId === requestId.current) setRefreshing(false); });
  }, []);

  useEffect(() => {
    fetchPage(1, { search: debounced, status, reportType, modalityId, bodyPartId, dateFrom, dateTo });
  }, [debounced, status, reportType, modalityId, bodyPartId, dateFrom, dateTo, fetchPage]);

  const clearFilters = () => { setStatus(''); setReportType(''); setModalityId(''); setBodyPartId(''); setDateFrom(''); setDateTo(''); };

  const fmt = (iso) =>
    new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalPages = pagination.totalPages || 1;
  const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);

  const goToPage = (p) => { if (p !== page) fetchPage(p, { search: debounced, status, reportType, modalityId, bodyPartId, dateFrom, dateTo }); };

  return (
    <div className="reports-page">
      <div className="list-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>All Reports</h1>
        {/* {pagination.total > 0 && (
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, pagination.total)} of {pagination.total}
          </span>
        )} */}
      </div>

      <div className="rl-toolbar">
        <div className="rl-search">
          <SearchIcon sx={{ fontSize: 18 }} />
          <input
            type="text"
            placeholder="Search by modality or body part…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className={`rl-filters-btn ${filtersOpen ? 'open' : ''}`} onClick={() => setFiltersOpen(o => !o)}>
          <FilterAltIcon sx={{ fontSize: 17 }} />
          Filters
          {activeFilterCount > 0 && <span className="rl-filter-count">{activeFilterCount}</span>}
        </button>
      </div>

      {filtersOpen && (
        <div className="rl-filters-panel">
          <div className="rl-filter-field">
            <label>Status</label>
            <FilterDropdown value={status} onChange={setStatus} options={STATUS_OPTIONS} align="left" />
          </div>
          <div className="rl-filter-field">
            <label>Type</label>
            <FilterDropdown value={reportType} onChange={setReportType} options={TYPE_OPTIONS} align="left" />
          </div>
          <div className="rl-filter-field">
            <label>Modality</label>
            <FilterDropdown
              value={modalityId}
              onChange={setModalityId}
              options={[{ value: '', label: 'All' }, ...modalities.map(m => ({ value: m.id, label: m.name }))]}
              align="left"
            />
          </div>
          <div className="rl-filter-field">
            <label>Body Part</label>
            <FilterDropdown
              value={bodyPartId}
              onChange={setBodyPartId}
              options={[{ value: '', label: 'All' }, ...bodyParts.map(b => ({ value: b.id, label: b.name }))]}
              align="left"
            />
          </div>
          <div className="rl-filter-field rl-daterange-wrap" ref={dateRangeRef}>
            <label>Date Range</label>
            <button
              type="button"
              className="rl-daterange-trigger"
              onClick={() => setDateRangeOpen(o => !o)}
            >
              <span className={(dateFrom || dateTo) ? '' : 'rl-daterange-placeholder'}>{dateRangeLabel}</span>
              <CalendarTodayIcon sx={{ fontSize: 14 }} />
            </button>

            {dateRangeOpen && (
              <div className="rl-daterange-popover">
                <div className="rl-daterange-field">
                  <label>Start Date</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} max={dateTo || undefined} />
                </div>
                <div className="rl-daterange-field">
                  <label>End Date</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} min={dateFrom || undefined} />
                </div>
              </div>
            )}
          </div>
          {activeFilterCount > 0 && (
            <button className="rl-clear-filters" onClick={clearFilters}>
              <CloseIcon sx={{ fontSize: 14 }} /> Clear filters
            </button>
          )}
        </div>
      )}

      <div className={`table-card ${refreshing ? 'is-refreshing' : ''}`}>
        {firstLoad ? (
          <SkeletonTable rows={PAGE_SIZE} />
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            {activeFilterCount > 0 || debounced ? (
              <>
                <div className="empty-title">No reports match your filters</div>
                <div className="empty-sub">Try adjusting or clearing the filters above.</div>
                <button className="empty-cta" onClick={() => { clearFilters(); setSearch(''); }}>Clear filters</button>
              </>
            ) : (
              <>
                <div className="empty-title">No reports found</div>
                <div className="empty-sub">Reports you convert will appear here. Start by creating a new report.</div>
                <a href="/reports/new" className="empty-cta">+ Create New Report</a>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="reports-table">
                <colgroup>
                  <col /><col /><col /><col /><col /><col /><col />
                </colgroup>
                <thead>
                  <tr>
                    <th>Date</th><th>Modality</th><th>Body Part</th><th>Technique</th><th>Type</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id}>
                      <td className="td-date">{fmt(r.updated_at || r.created_at)}</td>
                      <td>{r.modalities?.name ? <span className="modality-pill">{r.modalities.name}</span> : <span className="na">—</span>}</td>
                      <td className="td-body">{r.body_parts?.name || <span className="na">—</span>}</td>
                      <td className="td-body">{r.techniques?.name || <span className="na">—</span>}</td>
                      <td>
                        {r.report_type
                          ? <span className={`type-pill ${r.report_type}`}>{r.report_type === 'hospital' ? 'Hospital' : 'Non Hospital'}</span>
                          : <span className="na">—</span>}
                      </td>
                      <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                      <td>
                        <Link to={`/reports/${r.id}`} className="view-icon-btn">
                          <VisibilityIcon sx={{ fontSize: 13 }} /> View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <span className="pg-info">
                Showing {pagination.total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0} to {Math.min(page * PAGE_SIZE, pagination.total || 0)} of {pagination.total || 0} entries
              </span>
              <div className="pg-controls">
                <button className="pg-btn pg-btn-word" onClick={() => goToPage(page - 1)} disabled={page === 1}>Previous</button>
                {pageNums.map((p, i) =>
                  p === '...'
                    ? <span key={`e${i}`} className="pg-ellipsis">…</span>
                    : <button key={p} className={`pg-btn ${page === p ? 'active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
                )}
                <button className="pg-btn pg-btn-word" onClick={() => goToPage(page + 1)} disabled={page === totalPages}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


const SkeletonTable = ({ rows }) => (
  <div className="table-scroll">
    <table className="reports-table">
      <colgroup><col /><col /><col /><col /><col /><col /><col /></colgroup>
      <thead>
        <tr><th>Date</th><th>Modality</th><th>Body Part</th><th>Technique</th><th>Type</th><th>Status</th><th></th></tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <tr key={i} className="skeleton-row">
            <td><div className="sk-bar" style={{ width: '70%' }} /></td>
            <td><div className="sk-bar" style={{ width: '50%' }} /></td>
            <td><div className="sk-bar" style={{ width: '65%' }} /></td>
            <td><div className="sk-bar" style={{ width: '55%' }} /></td>
            <td><div className="sk-bar" style={{ width: '50%' }} /></td>
            <td><div className="sk-bar" style={{ width: '55%' }} /></td>
            <td><div className="sk-bar" style={{ width: '40%' }} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ReportsList;